/**
 * Map Generator
 * 
 * Generates terrain maps using a multi-scale approach:
 * 1. Macro layer: Continental features using Simplex noise
 * 2. Meso layer: Regional clustering
 * 3. Micro layer: Local variation using cellular automata
 */

import { createNoise2D } from 'simplex-noise';
import seedrandom from 'seedrandom';
import { TerrainType, TerrainMap, TerrainHex, AxialCoord } from '../types/terrain';
import { hexKey, hexNeighbors, isInBounds } from './hexMath';
import { applyFactionClustering } from './factions';

/**
 * Terrain probability matrix for cellular automata
 * Defines likelihood of terrain types appearing next to each other
 */
const TERRAIN_PROBABILITIES: Record<TerrainType, Record<TerrainType, number>> = {
  [TerrainType.Mountain]: {
    [TerrainType.Mountain]: 0.35,
    [TerrainType.Hills]: 0.30,
    [TerrainType.Tundra]: 0.15,
    [TerrainType.Forest]: 0.10,
    [TerrainType.River]: 0.05,
    [TerrainType.Settlement]: 0.00,
    [TerrainType.Desert]: 0.05,
    [TerrainType.Ocean]: 0.00,
  },
  [TerrainType.Forest]: {
    [TerrainType.Forest]: 0.50,
    [TerrainType.Hills]: 0.20,
    [TerrainType.River]: 0.15,
    [TerrainType.Settlement]: 0.10,
    [TerrainType.Tundra]: 0.03,
    [TerrainType.Mountain]: 0.02,
    [TerrainType.Desert]: 0.00,
    [TerrainType.Ocean]: 0.00,
  },
  [TerrainType.Hills]: {
    [TerrainType.Hills]: 0.30,
    [TerrainType.Forest]: 0.25,
    [TerrainType.Mountain]: 0.15,
    [TerrainType.River]: 0.15,
    [TerrainType.Tundra]: 0.10,
    [TerrainType.Settlement]: 0.05,
    [TerrainType.Desert]: 0.00,
    [TerrainType.Ocean]: 0.00,
  },
  [TerrainType.Tundra]: {
    [TerrainType.Tundra]: 0.45,
    [TerrainType.Mountain]: 0.25,
    [TerrainType.Hills]: 0.20,
    [TerrainType.River]: 0.05,
    [TerrainType.Forest]: 0.03,
    [TerrainType.Settlement]: 0.00,
    [TerrainType.Desert]: 0.02,
    [TerrainType.Ocean]: 0.00,
  },
  [TerrainType.Settlement]: {
    [TerrainType.Forest]: 0.25,
    [TerrainType.Hills]: 0.25,
    [TerrainType.River]: 0.20,
    [TerrainType.Tundra]: 0.15,
    [TerrainType.Settlement]: 0.10,
    [TerrainType.Mountain]: 0.05,
    [TerrainType.Desert]: 0.00,
    [TerrainType.Ocean]: 0.00,
  },
  [TerrainType.River]: {
    [TerrainType.River]: 0.30,
    [TerrainType.Forest]: 0.25,
    [TerrainType.Hills]: 0.20,
    [TerrainType.Tundra]: 0.10,
    [TerrainType.Settlement]: 0.10,
    [TerrainType.Mountain]: 0.05,
    [TerrainType.Desert]: 0.00,
    [TerrainType.Ocean]: 0.00,
  },
  [TerrainType.Desert]: {
    [TerrainType.Desert]: 0.50,
    [TerrainType.Hills]: 0.20,
    [TerrainType.Mountain]: 0.15,
    [TerrainType.River]: 0.10,
    [TerrainType.Tundra]: 0.05,
    [TerrainType.Forest]: 0.00,
    [TerrainType.Settlement]: 0.00,
    [TerrainType.Ocean]: 0.00,
  },
  [TerrainType.Ocean]: {
    [TerrainType.Ocean]: 1.00, // Ocean stays ocean
    [TerrainType.Mountain]: 0.00,
    [TerrainType.Tundra]: 0.00,
    [TerrainType.Hills]: 0.00,
    [TerrainType.Forest]: 0.00,
    [TerrainType.Settlement]: 0.00,
    [TerrainType.River]: 0.00,
    [TerrainType.Desert]: 0.00,
  },
};

/**
 * Generate a complete terrain map as an island
 */
export function generateMap(width: number, height: number, seed: string = 'storyworld'): TerrainMap {
  console.log(`Generating ${width}x${height} island map with seed: ${seed}`);
  const startTime = performance.now();
  
  const rng = seedrandom(seed);
  const noise2D = createNoise2D(rng);
  
  // Initialize map - all hexes start as ocean
  const hexes = new Map<string, TerrainHex>();
  for (let r = 0; r < height; r++) {
    for (let q = 0; q < width; q++) {
      hexes.set(hexKey({ q, r }), {
        q,
        r,
        terrain: TerrainType.Ocean,
        elevation: 0, // Sea level
      });
    }
  }
  
  // Layer 1: Generate island landmass mask
  console.log('Layer 1: Generating island shape...');
  const landMask = generateIslandMask(width, height, noise2D, rng);
  
  // Layer 2: Generate elevation map for land hexes
  console.log('Layer 2: Generating elevation map...');
  const elevationMap = generateElevationMap(width, height, noise2D);
  
  // Layer 2.5: Apply coastal elevation dampening for realistic beaches/lowlands
  console.log('Layer 2.5: Applying coastal elevation dampening...');
  applyCoastalDampening(elevationMap, landMask, width, height);
  
  // Layer 3: Assign terrain to land hexes based on elevation
  console.log('Layer 3: Assigning terrain to land...');
  for (let r = 0; r < height; r++) {
    for (let q = 0; q < width; q++) {
      const key = hexKey({ q, r });
      const hex = hexes.get(key)!;
      
      if (landMask[r][q]) {
        // This is land - assign terrain based on elevation
        const elevation = elevationMap[r][q];
        const latitude = r / height;
        const terrain = determineTerrainFromElevation(elevation, latitude, rng);
        
        hex.terrain = terrain;
        hex.elevation = Math.floor((elevation + 1) * 127.5); // Normalize to 0-255
      } else {
        // This is ocean - keep as ocean
        hex.terrain = TerrainType.Ocean;
        hex.elevation = 0;
      }
    }
  }
  
  // Layer 4: Add inlets and bays to coastline (DISABLED - noise already provides natural coastline)
  // console.log('Layer 4: Adding inlets and bays...');
  // addInletsAndBays(hexes, width, height, landMask, noise2D, rng);
  
  // Layer 5: Micro - Apply cellular automata for clustering (land only)
  console.log('Layer 5: Applying cellular automata...');
  applyCellularAutomata(hexes, width, height, rng, 3);
  
  // Layer 6: Generate rivers (land only, flow to ocean)
  console.log('Layer 6: Generating rivers...');
  generateRivers(hexes, width, height, elevationMap, rng, landMask);
  
  // Layer 7: Place settlements (land only)
  console.log('Layer 7: Placing settlements...');
  placeSettlements(hexes, width, height, rng, 0.15, landMask);
  
  // Layer 8: Assign factions to land hexes with contiguous clustering
  console.log('Layer 8: Assigning factions with territorial clustering...');
  assignFactionsToMap(hexes, width, height);
  
  const endTime = performance.now();
  console.log(`Island map generation complete in ${(endTime - startTime).toFixed(2)}ms`);
  
  // Find a land hex near the center for starting position
  const centerQ = Math.floor(width / 2);
  const centerR = Math.floor(height / 2);
  let startPosition: AxialCoord | null = null;
  
  // Search for nearest land hex to center
  for (let radius = 0; radius < Math.min(width, height) / 2; radius++) {
    for (let dq = -radius; dq <= radius; dq++) {
      for (let dr = -radius; dr <= radius; dr++) {
        const q = centerQ + dq;
        const r = centerR + dr;
        if (q >= 0 && q < width && r >= 0 && r < height) {
          const hex = hexes.get(hexKey({ q, r }));
          if (hex && hex.terrain !== TerrainType.Ocean) {
            startPosition = { q, r };
            break;
          }
        }
      }
      if (startPosition) break;
    }
    if (startPosition) break;
  }
  
  // Store starting position in map metadata (we'll use this in App.tsx)
  const mapResult = {
    width,
    height,
    hexes,
    seed,
    startPosition: startPosition || { q: centerQ, r: centerR }, // Fallback to center if no land found
  };
  
  return mapResult;
}

/**
 * Generate island landmass mask using noise
 * Creates an irregular island shape with natural coastline
 */
function generateIslandMask(
  width: number,
  height: number,
  noise2D: (x: number, y: number) => number,
  rng: () => number
): boolean[][] {
  const mask: boolean[][] = [];
  const centerX = width / 2;
  const centerY = height / 2;
  
  // Convert center to pixel space for accurate distance calculations
  const SQRT3 = Math.sqrt(3);
  const pixelCenterX = 1.5 * centerX;
  const pixelCenterY = SQRT3 * (centerY + centerX / 2);
  
  // Define 16:9 aspect ratio ellipse in pixel space
  // Use 45% of map dimensions for the island
  const radiusX = width * 0.45 * 1.5;        // Horizontal radius (wider for 16:9)
  const radiusY = height * 0.45 * SQRT3;     // Vertical radius (narrower)
  
  for (let r = 0; r < height; r++) {
    mask[r] = [];
    for (let q = 0; q < width; q++) {
      // Convert hex coordinates to pixel space for accurate distance measurement
      const pixelX = 1.5 * q;
      const pixelY = SQRT3 * (r + q / 2);
      
      // Distance from center in pixel space
      const dx = pixelX - pixelCenterX;
      const dy = pixelY - pixelCenterY;
      
      // Elliptical distance (normalized to 0-1 range)
      const ellipseDistance = Math.sqrt(
        (dx * dx) / (radiusX * radiusX) + 
        (dy * dy) / (radiusY * radiusY)
      );
      
      // Base island shape (1 at center, 0 at edge)
      const baseShape = 1 - ellipseDistance;
      
      // Add noise for irregular coastline
      const noiseScale = 0.05;
      const noiseValue = noise2D(q * noiseScale, r * noiseScale);
      const noiseVariation = noiseValue * 0.17; // 17% variation - irregular coastline
      
      // Combine base shape with noise
      const landValue = baseShape + noiseVariation;
      
      // Constant threshold for smooth coastline (lower = larger island)
      const threshold = 0.2;
      
      mask[r][q] = landValue > threshold;
    }
  }
  
  return mask;
}

/**
 * Add inlets and bays to the coastline
 * Erodes the coastline in certain areas to create water features
 */
function addInletsAndBays(
  hexes: Map<string, TerrainHex>,
  width: number,
  height: number,
  landMask: boolean[][],
  noise2D: (x: number, y: number) => number,
  rng: () => number
): void {
  // Find coastline hexes (land hexes adjacent to ocean)
  const coastline: Array<{ q: number; r: number; oceanNeighbors: number }> = [];
  
  for (let r = 0; r < height; r++) {
    for (let q = 0; q < width; q++) {
      if (!landMask[r][q]) continue; // Skip ocean hexes
      
      // Count ocean neighbors
      const coord: AxialCoord = { q, r };
      const neighbors = hexNeighbors(coord).filter(n => isInBounds(n, width, height));
      let oceanNeighbors = 0;
      
      for (const neighbor of neighbors) {
        if (!landMask[neighbor.r][neighbor.q]) {
          oceanNeighbors++;
        }
      }
      
      // If has ocean neighbors, it's on the coastline
      if (oceanNeighbors > 0) {
        coastline.push({ q, r, oceanNeighbors });
      }
    }
  }
  
  // Create inlets/bays by converting some coastline hexes to ocean
  // Use noise to determine which areas get eroded
  const numInlets = Math.floor(coastline.length * 0.15); // 15% of coastline becomes inlets
  let created = 0;
  
  for (const coastHex of coastline) {
    if (created >= numInlets) break;
    
    // Use noise to determine if this area should have an inlet
    const noiseValue = noise2D(coastHex.q * 0.1, coastHex.r * 0.1);
    
    // Higher noise + more ocean neighbors = more likely to be inlet
    const inletChance = (noiseValue + 1) / 2; // Normalize to 0-1
    const oceanFactor = coastHex.oceanNeighbors / 6; // 0-1 based on ocean neighbors
    
    // Combine factors - prefer areas with more ocean neighbors
    const combinedChance = inletChance * 0.4 + oceanFactor * 0.6;
    
    if (combinedChance > 0.65 && rng() < 0.3) {
      // Convert this hex and some neighbors to ocean (create inlet)
      const coord: AxialCoord = { q: coastHex.q, r: coastHex.r };
      const key = hexKey(coord);
      const hex = hexes.get(key);
      
      if (hex && hex.terrain !== TerrainType.Settlement) {
        hex.terrain = TerrainType.Ocean;
        landMask[coastHex.r][coastHex.q] = false;
        created++;
        
        // Sometimes extend the inlet inward
        if (rng() < 0.4) {
          const neighbors = hexNeighbors(coord).filter(n => isInBounds(n, width, height));
          for (const neighbor of neighbors) {
            if (landMask[neighbor.r][neighbor.q] && rng() < 0.5) {
              const neighborKey = hexKey(neighbor);
              const neighborHex = hexes.get(neighborKey);
              if (neighborHex && neighborHex.terrain !== TerrainType.Settlement) {
                neighborHex.terrain = TerrainType.Ocean;
                landMask[neighbor.r][neighbor.q] = false;
              }
            }
          }
        }
      }
    }
  }
  
  console.log(`  Created ${created} inlets/bays`);
}

/**
 * Generate elevation map using Simplex noise
 */
function generateElevationMap(
  width: number,
  height: number,
  noise2D: (x: number, y: number) => number
): number[][] {
  const elevationMap: number[][] = [];
  const scale = 0.02; // Lower = larger features
  
  for (let r = 0; r < height; r++) {
    elevationMap[r] = [];
    for (let q = 0; q < width; q++) {
      // Multiple octaves for detail at different scales
      let elevation = 0;
      let amplitude = 1;
      let frequency = 1;
      
      // 4 octaves
      for (let octave = 0; octave < 4; octave++) {
        elevation += noise2D(q * scale * frequency, r * scale * frequency) * amplitude;
        amplitude *= 0.5; // Each octave contributes less
        frequency *= 2;   // Each octave has higher frequency
      }
      
      // Normalize to -1 to 1
      elevation = elevation / 1.875; // Sum of amplitudes: 1 + 0.5 + 0.25 + 0.125
      elevationMap[r][q] = elevation;
    }
  }
  
  return elevationMap;
}

/**
 * Apply coastal elevation dampening to create realistic beaches and lowlands
 * Reduces elevation near coastlines to prevent mountains at the shore
 */
function applyCoastalDampening(
  elevationMap: number[][],
  landMask: boolean[][],
  width: number,
  height: number
): void {
  const DAMPENING_DISTANCE = 8; // Hexes from coast to apply dampening
  const MIN_DAMPENING = 0.3; // Minimum multiplier at coast (30% of original elevation)
  
  // For each land hex, calculate distance to nearest ocean
  const coastalDistances: number[][] = [];
  
  for (let r = 0; r < height; r++) {
    coastalDistances[r] = [];
    for (let q = 0; q < width; q++) {
      if (!landMask[r][q]) {
        coastalDistances[r][q] = 0; // Ocean
        continue;
      }
      
      // Find distance to nearest ocean hex
      let minDistance = DAMPENING_DISTANCE + 1;
      
      for (let searchRadius = 1; searchRadius <= DAMPENING_DISTANCE; searchRadius++) {
        let foundOcean = false;
        
        // Check hexes in a ring at this radius
        for (let dq = -searchRadius; dq <= searchRadius; dq++) {
          for (let dr = -searchRadius; dr <= searchRadius; dr++) {
            if (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr) !== searchRadius * 2) {
              continue; // Not on the ring at this radius
            }
            
            const checkQ = q + dq;
            const checkR = r + dr;
            
            if (checkQ >= 0 && checkQ < width && checkR >= 0 && checkR < height) {
              if (!landMask[checkR][checkQ]) {
                foundOcean = true;
                minDistance = Math.min(minDistance, searchRadius);
                break;
              }
            }
          }
          if (foundOcean) break;
        }
        if (foundOcean) break;
      }
      
      coastalDistances[r][q] = minDistance;
    }
  }
  
  // Apply dampening based on distance
  for (let r = 0; r < height; r++) {
    for (let q = 0; q < width; q++) {
      if (!landMask[r][q]) continue; // Skip ocean
      
      const distance = coastalDistances[r][q];
      
      if (distance <= DAMPENING_DISTANCE) {
        // Calculate dampening factor using smooth curve
        const t = distance / DAMPENING_DISTANCE; // 0 at coast, 1 at max distance
        const smoothT = t * t * (3 - 2 * t); // Smoothstep function
        
        // Interpolate between MIN_DAMPENING and 1.0
        const dampeningFactor = MIN_DAMPENING + (1.0 - MIN_DAMPENING) * smoothT;
        
        // Apply dampening
        elevationMap[r][q] *= dampeningFactor;
      }
    }
  }
  
  console.log(`  Coastal dampening applied (${DAMPENING_DISTANCE} hex range, ${MIN_DAMPENING * 100}% minimum)`);
}

/**
 * Determine terrain type from elevation and latitude
 */
function determineTerrainFromElevation(
  elevation: number,
  latitude: number,
  rng: () => number
): TerrainType {
  // Normalize elevation to 0-1
  const e = (elevation + 1) / 2;
  
  // High elevation = mountains
  if (e > 0.75) {
    return TerrainType.Mountain;
  }
  
  // High latitude = tundra (polar regions)
  if (latitude < 0.15 || latitude > 0.85) {
    if (e > 0.6) {
      return TerrainType.Mountain;
    }
    return TerrainType.Tundra;
  }
  
  // Low latitude + low elevation = desert (tropical/subtropical)
  if (latitude > 0.3 && latitude < 0.7 && e < 0.4 && rng() < 0.15) {
    return TerrainType.Desert;
  }
  
  // Medium-high elevation = hills
  if (e > 0.55) {
    return TerrainType.Hills;
  }
  
  // Medium elevation + moderate latitude = forest, hills, or grassland
  if (e > 0.35) {
    const rand = rng();
    if (rand > 0.5) {
      return TerrainType.Forest;
    } else if (rand > 0.25) {
      return TerrainType.Hills;
    } else {
      return TerrainType.Tundra; // Grassland/plains
    }
  }
  
  // Low elevation = forest, hills, or grassland (variety)
  const rand = rng();
  if (rand > 0.5) {
    return TerrainType.Forest;
  } else if (rand > 0.3) {
    return TerrainType.Hills;
  } else {
    return TerrainType.Tundra; // Grassland/plains
  }
}

/**
 * Apply cellular automata rules for terrain clustering
 */
function applyCellularAutomata(
  hexes: Map<string, TerrainHex>,
  width: number,
  height: number,
  rng: () => number,
  iterations: number
): void {
  for (let iter = 0; iter < iterations; iter++) {
    const updates = new Map<string, TerrainType>();
    
    for (let r = 0; r < height; r++) {
      for (let q = 0; q < width; q++) {
        const coord: AxialCoord = { q, r };
        const key = hexKey(coord);
        const hex = hexes.get(key);
        
        if (!hex) continue;
        
        // Skip settlements, rivers, and ocean - they don't cluster
        if (hex.terrain === TerrainType.Settlement || 
            hex.terrain === TerrainType.River || 
            hex.terrain === TerrainType.Ocean) continue;
        
        // Get neighbors
        const neighbors = hexNeighbors(coord)
          .filter(n => isInBounds(n, width, height))
          .map(n => hexes.get(hexKey(n))!)
          .filter(h => h !== undefined);
        
        // Count terrain types in neighbors
        const terrainCounts = new Map<TerrainType, number>();
        for (const neighbor of neighbors) {
          const count = terrainCounts.get(neighbor.terrain) || 0;
          terrainCounts.set(neighbor.terrain, count + 1);
        }
        
        // Select new terrain based on probabilities
        const newTerrain = selectTerrainByProbability(
          hex.terrain,
          terrainCounts,
          rng,
          0.15 // 15% chance to ignore rules for variety
        );
        
        if (newTerrain !== hex.terrain) {
          updates.set(key, newTerrain);
        }
      }
    }
    
    // Apply updates
    Array.from(updates.entries()).forEach(([key, terrain]) => {
      const hex = hexes.get(key)!;
      hex.terrain = terrain;
    });
    
    console.log(`  Iteration ${iter + 1}: ${updates.size} hexes updated`);
  }
}

/**
 * Select terrain type based on neighbor probabilities
 */
function selectTerrainByProbability(
  currentTerrain: TerrainType,
  neighborCounts: Map<TerrainType, number>,
  rng: () => number,
  randomnessChance: number
): TerrainType {
  // Random chance to ignore rules (prevents perfect uniformity)
  if (rng() < randomnessChance) {
    return currentTerrain;
  }
  
  // If no neighbors, keep current
  if (neighborCounts.size === 0) {
    return currentTerrain;
  }
  
  // Calculate weighted probabilities based on neighbors
  const weights = new Map<TerrainType, number>();
  const probabilities = TERRAIN_PROBABILITIES[currentTerrain];
  
  Array.from(neighborCounts.entries()).forEach(([terrain, count]) => {
    const baseProb = probabilities[terrain] || 0.01;
    weights.set(terrain, baseProb * count);
  });
  
  // Add current terrain with its self-probability
  const currentWeight = probabilities[currentTerrain] || 0.3;
  weights.set(currentTerrain, currentWeight * 2); // Bias toward staying same
  
  // Select based on weighted random
  const totalWeight = Array.from(weights.values()).reduce((sum, w) => sum + w, 0);
  let random = rng() * totalWeight;
  
  const weightsArray = Array.from(weights.entries());
  for (let i = 0; i < weightsArray.length; i++) {
    const [terrain, weight] = weightsArray[i];
    random -= weight;
    if (random <= 0) {
      return terrain;
    }
  }
  
  return currentTerrain;
}

/**
 * Generate rivers flowing from high to low elevation (land only, flow to ocean)
 */
function generateRivers(
  hexes: Map<string, TerrainHex>,
  width: number,
  height: number,
  elevationMap: number[][],
  rng: () => number,
  landMask?: boolean[][]
): void {
  // Count land hexes for river density
  const landHexes = landMask 
    ? landMask.flat().filter(Boolean).length 
    : width * height;
  const numRivers = Math.floor(landHexes / 500); // ~1 river per 500 land hexes
  let placed = 0;
  
  console.log(`  Attempting to generate ${numRivers} rivers...`);
  
  // Find high elevation starting points on land (mountain sources)
  const candidates: Array<{ q: number; r: number; elevation: number }> = [];
  for (let r = 0; r < height; r++) {
    for (let q = 0; q < width; q++) {
      // Only consider land hexes
      if (landMask && !landMask[r][q]) continue;
      
      const elevation = elevationMap[r][q];
      const hex = hexes.get(hexKey({ q, r }));
      if (elevation > 0.5 && hex && hex.terrain !== TerrainType.Ocean) {
        candidates.push({ q, r, elevation });
      }
    }
  }
  
  // Sort by elevation (highest first) and take top candidates
  candidates.sort((a, b) => b.elevation - a.elevation);
  const riverSources = candidates.slice(0, numRivers * 3); // More sources than rivers
  
  // Generate rivers from random sources
  for (let i = 0; i < numRivers && i < riverSources.length; i++) {
    const source = riverSources[Math.floor(rng() * riverSources.length)];
    const startCoord: AxialCoord = { q: source.q, r: source.r };
    
    // Flow downhill to ocean
    let current = startCoord;
    const riverPath: AxialCoord[] = [];
    const visited = new Set<string>();
    
    while (isInBounds(current, width, height) && riverPath.length < 100) {
      const key = hexKey(current);
      if (visited.has(key)) break; // Avoid loops
      visited.add(key);
      
      const hex = hexes.get(key);
      if (!hex) break;
      
      // Stop if we hit ocean
      if (hex.terrain === TerrainType.Ocean) {
        break; // River reached the sea
      }
      
      riverPath.push({ ...current });
      
      // Don't override settlements
      if (hex.terrain !== TerrainType.Settlement) {
        hex.terrain = TerrainType.River;
      }
      
      // Find lowest neighbor (downhill), prefer ocean if adjacent
      const neighbors = hexNeighbors(current).filter(n => isInBounds(n, width, height));
      let lowest = current;
      let lowestElevation = elevationMap[current.r][current.q];
      let foundOcean = false;
      
      // First check for adjacent ocean
      for (const neighbor of neighbors) {
        const neighborHex = hexes.get(hexKey(neighbor));
        if (neighborHex && neighborHex.terrain === TerrainType.Ocean) {
          lowest = neighbor;
          foundOcean = true;
          break;
        }
      }
      
      // If no adjacent ocean, find lowest land neighbor
      if (!foundOcean) {
        for (const neighbor of neighbors) {
          if (visited.has(hexKey(neighbor))) continue;
          // Skip ocean neighbors (we want to flow to them, not through them)
          const neighborHex = hexes.get(hexKey(neighbor));
          if (neighborHex && neighborHex.terrain === TerrainType.Ocean) {
            lowest = neighbor;
            foundOcean = true;
            break;
          }
          // Only consider land hexes
          if (landMask && !landMask[neighbor.r][neighbor.q]) continue;
          
          const elev = elevationMap[neighbor.r][neighbor.q];
          if (elev < lowestElevation) {
            lowest = neighbor;
            lowestElevation = elev;
          }
        }
      }
      
      // If no downhill neighbor, stop
      if (lowest === current && !foundOcean) break;
      
      current = lowest;
    }
    
    if (riverPath.length > 5) {
      placed++;
    }
  }
  
  console.log(`  Generated ${placed} rivers`);
}

/**
 * Place settlements on the map (land only)
 */
function placeSettlements(
  hexes: Map<string, TerrainHex>,
  width: number,
  height: number,
  rng: () => number,
  density: number, // settlements per 100 hexes
  landMask?: boolean[][]
): void {
  // Count land hexes for settlement density
  const landHexes = landMask 
    ? landMask.flat().filter(Boolean).length 
    : width * height;
  const numSettlements = Math.floor(landHexes * density / 100);
  let placed = 0;
  
  console.log(`  Attempting to place ${numSettlements} settlements...`);
  
  // Try to place settlements on land only
  for (let attempt = 0; attempt < numSettlements * 10 && placed < numSettlements; attempt++) {
    const q = Math.floor(rng() * width);
    const r = Math.floor(rng() * height);
    
    // Only consider land hexes
    if (landMask && !landMask[r][q]) continue;
    
    const coord: AxialCoord = { q, r };
    const key = hexKey(coord);
    const hex = hexes.get(key);
    
    if (!hex) continue;
    
    // Don't place on ocean
    if (hex.terrain === TerrainType.Ocean) continue;
    
    // Check if suitable for settlement
    if (isSettlementSuitable(coord, hexes, width, height)) {
      hex.terrain = TerrainType.Settlement;
      placed++;
    }
  }
  
  console.log(`  Placed ${placed} settlements`);
}

/**
 * Check if a location is suitable for a settlement
 */
function isSettlementSuitable(
  coord: AxialCoord,
  hexes: Map<string, TerrainHex>,
  width: number,
  height: number
): boolean {
  const hex = hexes.get(hexKey(coord));
  if (!hex) return false;
  
  // Don't place on mountains, tundra, rivers, deserts, or ocean
  if (hex.terrain === TerrainType.Mountain || 
      hex.terrain === TerrainType.Tundra ||
      hex.terrain === TerrainType.River ||
      hex.terrain === TerrainType.Desert ||
      hex.terrain === TerrainType.Ocean) {
    return false;
  }
  
  // Don't place on existing settlements
  if (hex.terrain === TerrainType.Settlement) {
    return false;
  }
  
  // Check for nearby settlements (minimum 15 hexes apart)
  const neighbors = hexNeighbors(coord);
  for (let ring = 1; ring <= 15; ring++) {
    for (const neighbor of neighbors) {
      if (!isInBounds(neighbor, width, height)) continue;
      const neighborHex = hexes.get(hexKey(neighbor));
      if (neighborHex?.terrain === TerrainType.Settlement) {
        return false;
      }
    }
  }
  
  // Check for too many nearby mountains
  let mountainCount = 0;
  for (const neighbor of hexNeighbors(coord)) {
    if (!isInBounds(neighbor, width, height)) continue;
    const neighborHex = hexes.get(hexKey(neighbor));
    if (neighborHex?.terrain === TerrainType.Mountain) {
      mountainCount++;
    }
  }
  
  if (mountainCount > 3) {
    return false;
  }
  
  return true;
}

/**
 * Assign factions to land hexes using cellular automata clustering
 * Creates contiguous faction territories with natural wilderness borders
 */
function assignFactionsToMap(
  hexes: Map<string, TerrainHex>,
  width: number,
  height: number
): void {
  // Use new clustering approach for contiguous territories
  applyFactionClustering(hexes, width, height, 0.25, 3);
  
  // Count final distribution
  let factionCount = { 
    'highland-clans': 0, 
    'stoneheart-clans': 0, 
    'sea-kings': 0, 
    'hearth-folk': 0, 
    'wildwood-folk': 0, 
    'riverbend-kingdoms': 0, 
    wilderness: 0 
  };
  
  for (const hex of hexes.values()) {
    if (hex.terrain === TerrainType.Ocean) continue;
    
    if (hex.faction === 'highland-clans') factionCount['highland-clans']++;
    else if (hex.faction === 'stoneheart-clans') factionCount['stoneheart-clans']++;
    else if (hex.faction === 'sea-kings') factionCount['sea-kings']++;
    else if (hex.faction === 'hearth-folk') factionCount['hearth-folk']++;
    else if (hex.faction === 'wildwood-folk') factionCount['wildwood-folk']++;
    else if (hex.faction === 'riverbend-kingdoms') factionCount['riverbend-kingdoms']++;
    else factionCount.wilderness++;
  }
  
  console.log('Faction distribution:', factionCount);
}

/**
 * Get a hex from the map
 */
export function getHex(map: TerrainMap, coord: AxialCoord): TerrainHex | undefined {
  return map.hexes.get(hexKey(coord));
}

/**
 * Get terrain distribution statistics
 */
export function getTerrainDistribution(map: TerrainMap): Record<TerrainType, number> {
  const distribution: Record<TerrainType, number> = {
    [TerrainType.Mountain]: 0,
    [TerrainType.Tundra]: 0,
    [TerrainType.Hills]: 0,
    [TerrainType.Forest]: 0,
    [TerrainType.Settlement]: 0,
    [TerrainType.River]: 0,
    [TerrainType.Desert]: 0,
    [TerrainType.Ocean]: 0,
  };
  
  Array.from(map.hexes.values()).forEach((hex) => {
    distribution[hex.terrain]++;
  });
  
  return distribution;
}

/**
 * Get all settlement hexes from the map
 */
export function getSettlementHexes(map: TerrainMap): AxialCoord[] {
  const settlements: AxialCoord[] = [];
  
  Array.from(map.hexes.values()).forEach((hex) => {
    if (hex.terrain === TerrainType.Settlement) {
      settlements.push({ q: hex.q, r: hex.r });
    }
  });
  
  return settlements;
}

/**
 * Select a random settlement hex as spawn point
 * Falls back to (100, 100) if no settlements found
 */
export function selectRandomSpawnHex(map: TerrainMap): AxialCoord {
  const settlements = getSettlementHexes(map);
  
  if (settlements.length === 0) {
    console.warn('⚠️ No settlements found! Using default spawn (100, 100)');
    return { q: 100, r: 100 };
  }
  
  const randomIndex = Math.floor(Math.random() * settlements.length);
  const spawnHex = settlements[randomIndex];
  
  console.log(`🏠 Selected spawn hex: (${spawnHex.q}, ${spawnHex.r}) from ${settlements.length} settlements`);
  
  return spawnHex;
}

